// components/Dashboard/CommonChart.tsx
import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { Box, Paper, Typography } from "@mui/material";

type ChartType = "bar" | "line" | "pie";

interface CommonChartProps {
  title: string;
  type: ChartType;
  data: any[];
  dataKey: string;
  nameKey?: string;
  color?: string;
  colors?: string[];
  height?: number;
  noShadow?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Filter out the 'empty' entry
    const mainPayload = payload.find((p: any) => p.name !== 'empty');
    if (mainPayload) {
      const displayName = mainPayload.name.replace(/_/g, ' ');
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          <p style={{ margin: 0 }}>{displayName}: {mainPayload.value}</p>
        </div>
      );
    }
  }
  return null;
};

const CommonChart: React.FC<CommonChartProps> = ({
  title,
  type,
  data,
  dataKey,
  nameKey = "name",
  color = "#1976d2",
  colors,
  height = 300,
  noShadow = false,
}) => {
  const defaultColors = ["#7DE1E0", "#4DBCC0", "#2E929C", "#176570"];

  // Determine color mode
  const isMultiColor = Array.isArray(colors) && colors.length > 0;

  return (
    <Paper
      elevation={noShadow ? 0 : 2}
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: noShadow ? "none" : undefined,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Box sx={{ flexGrow: 1, minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%">
          {type === "bar" ? (
            <BarChart
              data={data.map((d) => ({
                ...d,
                empty: 400 - d[dataKey], // gray bottom portion
              }))}
              barSize={45}
              margin={{ top: 20, right: 20, left: 0, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey={nameKey}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#555", fontSize: 13 }}
                interval={0}
              />
              <YAxis
                domain={[0, 400]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#888", fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Colored top part */}
              <Bar dataKey={dataKey} stackId="a">
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      isMultiColor
                        ? colors![index % colors!.length]
                        : color || defaultColors[index % defaultColors.length]
                    }
                  />
                ))}
                {/* Label at top of color section
    <LabelList
      dataKey={dataKey}
      position="top"
      style={{
        fill: "#000",
        fontWeight: 700,
        fontSize: 14,
        zIndex:"10000"
      }}
    /> */}
              </Bar>
              <Bar
                dataKey="empty"
                stackId="a"
                fill="#f3f3f3"
                radius={[6, 6, 0, 0]}
              >
                <LabelList
                  dataKey={dataKey}
                  position="bottom"
                  dy={-15}
                  style={{
                    fill: "#000",
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                />
              </Bar>
            </BarChart>
          ) : type === "line" ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={nameKey} />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2}
              />
            </LineChart>
          ) : (
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                nameKey={nameKey}
                outerRadius={100}
                label
              >
                {data.map((_, i) => (
                  <Cell
                    key={i}
                    fill={
                      isMultiColor
                        ? colors![i % colors!.length]
                        : color || defaultColors[i % defaultColors.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default CommonChart;